import { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import IdolFilterForm from './IdolFilterForm';

// Form onChange 用 snake_case，filters 物件用 camelCase，這裡做雙向映射
const SNAKE_TO_CAMEL = {
  age_min: 'ageMin',
  age_max: 'ageMax',
  is_original: 'isOriginal',
  is_nsfw: 'isNsfw',
  sexual_orientation: 'sexualOrientation',
  relationship_status: 'relationshipStatus',
};

const EMPTY_FILTERS = {
  keyword: '',
  ageMin: 1,
  ageMax: 100,
  gender: [],
  sexualOrientation: [],
  relationshipStatus: [],
  preferences: [],
  isOriginal: '',
  isNsfw: '',
};

// camelCase filters → snake_case 批次 update，供父層 updateFilters 寫入 URL
const filtersToBatchUpdate = (f) => ({
  keyword: f.keyword,
  age_min: f.ageMin,
  age_max: f.ageMax,
  gender: f.gender,
  sexual_orientation: f.sexualOrientation,
  relationship_status: f.relationshipStatus,
  preferences: f.preferences,
  is_original: f.isOriginal,
  is_nsfw: f.isNsfw,
});

const IdolFilterModal = ({ show, onHide, filters, onFilterChange }) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState(filters);

  // Modal 開啟時把當前 filters 複製到 local state；關閉後丟棄
  useEffect(() => {
    if (show) setLocalFilters(filters);
    // 只在 show 變化時同步，避免外部 filters 變動時覆蓋使用者編輯中的暫存
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // 攔截 Form 的 snake_case onChange，轉成 camelCase 寫入 local state
  // 陣列要解包 react-select 的 {value, label} 物件，跟父層 updateFilters 的處理一致
  const handleLocalChange = (keyOrUpdates, value) => {
    setLocalFilters(prev => {
      const updates = typeof keyOrUpdates === 'object'
        ? keyOrUpdates
        : { [keyOrUpdates]: value };
      const next = { ...prev };
      for (const [k, v] of Object.entries(updates)) {
        const mappedKey = SNAKE_TO_CAMEL[k] || k;
        next[mappedKey] = Array.isArray(v)
          ? v.map(item => (typeof item === 'object' && item !== null ? item.value : item))
          : v;
      }
      return next;
    });
  };

  const handleApply = () => {
    onFilterChange(filtersToBatchUpdate(localFilters));
    onHide();
  };

  // Reset 只清空 local UI，要按 Apply 才真的生效
  const handleReset = () => {
    setLocalFilters(EMPTY_FILTERS);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{t('filter')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <IdolFilterForm filters={localFilters} onFilterChange={handleLocalChange} uniqueId="_modal" />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" size="sm" onClick={handleReset} className="rounded-pill">
          {t('reset')}
        </Button>
        <Button variant="primary" size="sm" onClick={handleApply} className="rounded-pill">
          {t('apply')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default IdolFilterModal;
